/**
 * Forage Store
 * Global state management for context-driven cross-era music discovery
 */

import { create } from 'zustand';
import type { ForageResponse } from '../types/forage.types';
import type { EnrichedTrack } from '../types/track.types';

interface ForageStore {
  // Input state
  seeking: string;
  context: string;
  avoid: string;

  // Results state
  forageId: string | null;
  interpretation: string | null;
  results: EnrichedTrack[];
  cacheHit: boolean;
  responseTimeMs: number;

  // Selection state
  selectedTracks: Set<string>;

  // UI state
  loading: boolean;
  error: string | null;

  // Input actions
  setSeeking: (seeking: string) => void;
  setContext: (context: string) => void;
  setAvoid: (avoid: string) => void;

  // Results actions
  setForageResults: (response: ForageResponse) => void;
  clearResults: () => void;

  // Selection actions
  toggleTrackSelection: (trackId: string) => void;
  clearSelection: () => void;

  // UI actions
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Computed
  getSelectedTracks: () => EnrichedTrack[];
}

export const useForageStore = create<ForageStore>((set, get) => ({
  // Initial state
  seeking: '',
  context: '',
  avoid: '',

  forageId: null,
  interpretation: null,
  results: [],
  cacheHit: false,
  responseTimeMs: 0,

  selectedTracks: new Set(),

  loading: false,
  error: null,

  setSeeking: (seeking) => set({ seeking }),
  setContext: (context) => set({ context }),
  setAvoid: (avoid) => set({ avoid }),

  setForageResults: (response) => {
    set({
      forageId: response.forage_id,
      interpretation: response.interpretation,
      results: response.recommendations,
      cacheHit: response.cache_hit,
      responseTimeMs: response.response_time_ms,
      loading: false,
      error: null,
      selectedTracks: new Set(),
    });
  },

  clearResults: () => {
    set({
      forageId: null,
      interpretation: null,
      results: [],
      cacheHit: false,
      responseTimeMs: 0,
      selectedTracks: new Set(),
      error: null,
    });
  },

  toggleTrackSelection: (trackId) => {
    const { selectedTracks } = get();
    const next = new Set(selectedTracks);
    if (next.has(trackId)) {
      next.delete(trackId);
    } else {
      next.add(trackId);
    }
    set({ selectedTracks: next });
  },

  clearSelection: () => {
    set({ selectedTracks: new Set() });
  },

  setLoading: (loading) => set({ loading }),

  setError: (error) => set({ error, loading: false }),

  getSelectedTracks: () => {
    const { results, selectedTracks } = get();
    return results.filter((t) => selectedTracks.has(t.id));
  },
}));
